import { CombinedGraphQLErrors } from "@apollo/client"
import { useMutation } from "@apollo/client/react"
import { zodResolver } from "@hookform/resolvers/zod"
import { REGEXP_ONLY_DIGITS_AND_CHARS } from "input-otp"
import React, { useState } from "react"
import { useForm } from "react-hook-form"
import { Link } from "react-router-dom"
import { toast } from "sonner"
import { v4 as uuidv4 } from "uuid"
import * as z from "zod"
import { gql } from "~/__generated__"
import { useUiAccess } from "~/auth/use-ui-access"
import { useViewerMaybe } from "~/auth/use-viewer"
import { useNavigateAfterAuth } from "~/auth/utils"
import { useFormErrorHandling } from "~/common/error-handling"
import { signupPath } from "~/common/paths"
import { Button } from "~/ui/button"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "~/ui/form"
import { TextField } from "~/ui/forms/fields/text-field"
import { FormGeneralErrors } from "~/ui/forms/form-general-errors"
import { InputOTP, InputOTPGroup, InputOTPSlot } from "~/ui/input-otp"
import { EMAIL_TOKEN_AUTH_MUTATION } from "./email-auth-screen"
import { updateCsrfTag } from "./utils"

const emailLoginFormSchema = z.object({
  email: z.email({ message: "Invalid email address" }),
})

const otpFormSchema = z.object({
  otp: z.string().length(6, {
    message: "Your one-time password must be 6 characters.",
  }),
})

type EmailLoginFormValues = z.infer<typeof emailLoginFormSchema>
type OtpFormValues = z.infer<typeof otpFormSchema>

const EMAIL_AUTH_CHALLENGE_MUTATION = gql(/* GraphQL */ `
  mutation EmailAuthChallenge($input: EmailUserAuthChallengeInput!) {
    emailUserAuthChallenge(input: $input) {
      success
    }
  }
`)

export const EmailLoginScreen: React.FC = () => {
  const [emailAuthChallenge, { loading: challengeLoading }] = useMutation(
    EMAIL_AUTH_CHALLENGE_MUTATION
  )
  const [emailTokenAuth, { loading: tokenAuthLoading }] = useMutation(EMAIL_TOKEN_AUTH_MUTATION)
  const [step, setStep] = useState<"email" | "otp" | "not_found">("email")
  const [email, setEmail] = useState("")
  const { navigateAfterAuth } = useNavigateAfterAuth()
  const {
    result: { refetch: viewerRefetch },
  } = useViewerMaybe()
  const { refetch: refetchUiAccess } = useUiAccess()

  const emailForm = useForm<EmailLoginFormValues>({
    resolver: zodResolver(emailLoginFormSchema),
    defaultValues: {
      email: "",
    },
  })

  const otpForm = useForm<OtpFormValues>({
    resolver: zodResolver(otpFormSchema),
    defaultValues: {
      otp: "",
    },
  })

  const { onError: onEmailFormError } = useFormErrorHandling(emailForm)
  const { onError: onOtpFormError } = useFormErrorHandling(otpForm)

  const onEmailSubmit = async (values: EmailLoginFormValues) => {
    const storedAuthCodes = JSON.parse(localStorage.getItem("authCodes") || "[]")
    let newClientAuthCode: string
    if (storedAuthCodes.length === 0) {
      newClientAuthCode = uuidv4()
      localStorage.setItem("authCodes", JSON.stringify([newClientAuthCode]))
    } else {
      newClientAuthCode = storedAuthCodes[storedAuthCodes.length - 1]
    }

    const { data, error } = await emailAuthChallenge({
      onError: (err) => {
        const isUserNotFound =
          CombinedGraphQLErrors.is(err) && err.errors.some((e) => e.message === "User not found")
        if (isUserNotFound) {
          setStep("not_found")
        } else {
          onEmailFormError(err)
        }
      },
      variables: {
        input: {
          email: values.email,
          clientAuthCode: newClientAuthCode,
        },
      },
    })

    if (error) {
      return
    }

    if (data?.emailUserAuthChallenge.success) {
      setEmail(values.email)
      setStep("otp")
      toast.success("Email Sent", {
        description: "Please check your email for the login link.",
      })
    } else {
      toast.error("Login Failed", {
        description: "Unable to send login email. Please try again.",
      })
    }
  }

  const onOtpSubmit = async (values: OtpFormValues) => {
    const { data, error } = await emailTokenAuth({
      onError: onOtpFormError,
      variables: {
        input: {
          email,
          token: values.otp,
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      },
    })

    if (error) {
      return
    }

    if (data?.emailTokenUserAuth.success) {
      updateCsrfTag(data.emailTokenUserAuth.csrfToken)
      await viewerRefetch()
      await refetchUiAccess()
      toast.success("Authentication Successful", {
        description: "You have been successfully logged in.",
      })
      navigateAfterAuth()
    } else {
      toast.error("Authentication Failed", {
        description: "Unable to authenticate. Please try again.",
      })
    }
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background">
      <div className="w-full max-w-md space-y-8">
        <h1 className="text-center text-2xl font-bold">Login with Email</h1>
        {step === "email" && (
          <Form {...emailForm}>
            <form onSubmit={emailForm.handleSubmit(onEmailSubmit)} className="space-y-6">
              <FormGeneralErrors control={emailForm.control} />
              <TextField
                control={emailForm.control}
                name="email"
                label="Email"
                placeholder="Enter your email"
                inputMode="email"
                autoComplete="email"
              />
              <Button type="submit" className="w-full" disabled={challengeLoading}>
                {challengeLoading ? "Sending..." : "Send Login Link"}
              </Button>
            </form>
          </Form>
        )}
        {step === "otp" && (
          <Form {...otpForm}>
            <form onSubmit={otpForm.handleSubmit(onOtpSubmit)} className="flex flex-col space-y-6">
              <FormGeneralErrors control={otpForm.control} />
              <FormField
                control={otpForm.control}
                name="otp"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[11px] font-semibold tracking-[0.15em] uppercase text-center">
                      Copy and paste the code in your email to log in
                    </FormLabel>
                    <FormControl>
                      <InputOTP
                        maxLength={6}
                        {...field}
                        pattern={REGEXP_ONLY_DIGITS_AND_CHARS}
                        inputMode="text"
                        type="text"
                        data-1p-ignore="true"
                      >
                        <InputOTPGroup>
                          <InputOTPSlot index={0} />
                          <InputOTPSlot index={1} />
                          <InputOTPSlot index={2} />
                          <InputOTPSlot index={3} />
                          <InputOTPSlot index={4} />
                          <InputOTPSlot index={5} />
                        </InputOTPGroup>
                      </InputOTP>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button type="submit" className="w-full" disabled={tokenAuthLoading}>
                {tokenAuthLoading ? "Verifying..." : "Continue with Login Code"}
              </Button>
            </form>
          </Form>
        )}
        {step === "not_found" && (
          <div className="space-y-4 text-center">
            <p className="text-sm text-stone-600">
              We didn&apos;t find an account for you. Request access now to see what&apos;s on
              radar.
            </p>
            <Link to={signupPath({})}>
              <Button className="w-full">Request access</Button>
            </Link>
            <button
              type="button"
              onClick={() => setStep("email")}
              className="text-sm text-muted-foreground underline underline-offset-4"
            >
              Try a different email
            </button>
          </div>
        )}
        {step !== "not_found" && (
          <div className="space-y-2 text-center">
            <p className="text-sm text-muted-foreground">
              Don&apos;t have an account?{" "}
              <Link to={signupPath({})} className="text-foreground underline underline-offset-4">
                Create Account
              </Link>
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
